import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import {
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  Switch,
  Spinner,
  Flex,
  Alert,
  AlertIcon,
  Box,
  ButtonGroup,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSave,
  FiX,
  FiChevronUp,
  FiChevronDown,
} from 'react-icons/fi';
import { MdDragIndicator } from 'react-icons/md';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

// Sortable Row Component
const SortableRow = React.memo(({ subTask, index, totalCount, onEdit, onDelete, onMoveUp, onMoveDown }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: subTask.id });

  const style = useMemo(() => ({
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition: transition || 'transform 150ms ease',
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? 'rgba(49, 130, 206, 0.2)' : 'transparent',
  }), [transform, transition, isDragging]);

  return (
    <Tr ref={setNodeRef} style={style}>
      <Td width="40px" p={2}>
        <Tooltip label="Drag to reorder">
          <Box
            {...attributes}
            {...listeners}
            cursor="grab"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="gray.400"
            _hover={{ color: "blue.400" }}
            _active={{ cursor: "grabbing" }}
          >
            <MdDragIndicator size={20} />
          </Box>
        </Tooltip>
      </Td>
      <Td width="80px" p={2}>
        <ButtonGroup size="xs" isAttached variant="outline">
          <IconButton
            aria-label="Move up"
            icon={<FiChevronUp />}
            onClick={() => onMoveUp(index)}
            isDisabled={index === 0}
            size="xs"
          />
          <IconButton
            aria-label="Move down"
            icon={<FiChevronDown />}
            onClick={() => onMoveDown(index)}
            isDisabled={index === totalCount - 1}
            size="xs"
          />
        </ButtonGroup>
      </Td>
      <Td whiteSpace="nowrap">
        <Badge colorScheme="blue" variant="outline">
          {subTask.categoryCode}
        </Badge>
      </Td>
      <Td whiteSpace="nowrap">
        <Badge colorScheme="green" variant="outline">
          {subTask.activityCode}
        </Badge>
      </Td>
      <Td color="white" maxW="300px">
        <Text fontWeight="medium" noOfLines={2}>
          {subTask.name}
        </Text>
      </Td>
      <Td color="white" whiteSpace="nowrap">
        {subTask.defaultProductivity}
      </Td>
      <Td color="white" whiteSpace="nowrap">{subTask.unit}</Td>
      <Td whiteSpace="nowrap">
        <Badge colorScheme={subTask.isActive ? "green" : "red"}>
          {subTask.isActive ? "Active" : "Inactive"}
        </Badge>
      </Td>
      <Td>
        <HStack spacing={2}>
          <IconButton
            aria-label="Edit"
            icon={<FiEdit2 />}
            size="sm"
            variant="outline"
            onClick={() => onEdit(subTask)}
          />
          <IconButton
            aria-label="Delete"
            icon={<FiTrash2 />}
            size="sm"
            variant="outline"
            colorScheme="red"
            onClick={() => onDelete(subTask)}
          />
        </HStack>
      </Td>
    </Tr>
  );
});

SortableRow.displayName = 'SortableRow';

const MasterSubTaskManager = ({ categories, onDataChange }) => {
  const [subTasks, setSubTasks] = useState([]);
  const [originalSubTasks, setOriginalSubTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubTask, setSelectedSubTask] = useState(null);
  const [formData, setFormData] = useState({
    masterActivityId: '',
    name: '',
    description: '',
    defaultProductivity: 0,
    unit: 'No',
    order: 0,
    isActive: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const {
    isOpen: isFormOpen,
    onOpen: onFormOpen,
    onClose: onFormClose
  } = useDisclosure();

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose
  } = useDisclosure();

  // Fast drag sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    })
  );

  const allActivities = useMemo(() => 
    categories
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .flatMap(category =>
        (category.masterActivities || [])
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map(activity => ({
            ...activity,
            categoryName: category.name,
            categoryCode: category.code,
            categoryOrder: category.order || 0
          }))
      ), [categories]
  );

  useEffect(() => {
    fetchAllSubTasks();
  }, [categories]);

  const fetchAllSubTasks = useCallback(async () => {
    setLoading(true);
    try {
      const allSubTasks = [];
      
      categories.forEach(category => {
        if (category.masterActivities) {
          category.masterActivities.forEach(activity => {
            if (activity.masterSubTasks) {
              allSubTasks.push(...activity.masterSubTasks.map(subTask => ({
                ...subTask,
                activityName: activity.name,
                activityOrder: activity.order,
                activityCode: activity.code,
                categoryName: category.name,
                categoryCode: category.code,
                categoryOrder: category.order || 0
              })));
            }
          });
        }
      });

      const sortedSubTasks = allSubTasks.sort((a, b) => {
        if (a.categoryOrder !== b.categoryOrder) return a.categoryOrder - b.categoryOrder;
        if (a.activityOrder !== b.activityOrder) return a.activityOrder - b.activityOrder;
        return (a.order || 0) - (b.order || 0);
      });

      setSubTasks(sortedSubTasks);
      setOriginalSubTasks(sortedSubTasks);
      setHasOrderChanges(false);
    } catch (error) {
      console.error('Error fetching sub-tasks:', error);
      toast.error('Failed to fetch sub-tasks');
    } finally {
      setLoading(false);
    }
  }, [categories]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSubTasks((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      setHasOrderChanges(true);
    }
  }, []);

  const moveUp = useCallback((index) => {
    if (index === 0) return;
    
    setSubTasks((items) => {
      const newItems = [...items];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      return newItems;
    });
    setHasOrderChanges(true);
  }, []);

  const moveDown = useCallback((index) => {
    setSubTasks((items) => {
      if (index === items.length - 1) return items;
      
      const newItems = [...items];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      return newItems;
    });
    setHasOrderChanges(true);
  }, []);

  const handleSaveOrder = useCallback(async () => {
    setSavingOrder(true);
    try {
      const updates = subTasks.map((subTask, index) => ({
        id: subTask.id,
        order: index
      }));

      const { data } = await api.put('/super-admin/master/subtasks/bulk-order', {
        subtasks: updates
      });

      if (data.success) {
        toast.success('Order saved');
        setOriginalSubTasks([...subTasks]);
        setHasOrderChanges(false);
        onDataChange?.();
      } else {
        toast.error(data.message || 'Failed');
      }
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Failed to save');
    } finally {
      setSavingOrder(false);
    }
  }, [subTasks, onDataChange]);

  const handleCancelOrder = useCallback(() => {
    setSubTasks([...originalSubTasks]);
    setHasOrderChanges(false);
    toast.success('Cancelled');
  }, [originalSubTasks]);

  const handleCreate = useCallback(() => {
    setSelectedSubTask(null);
    setFormData({
      masterActivityId: allActivities[0]?.id || '',
      name: '',
      description: '',
      defaultProductivity: 0,
      unit: 'No',
      order: 0,
      isActive: true
    });
    setIsEditing(false);
    onFormOpen();
  }, [allActivities, onFormOpen]);

  const handleEdit = useCallback((subTask) => {
    setSelectedSubTask(subTask);
    setFormData({
      masterActivityId: subTask.masterActivityId,
      name: subTask.name,
      description: subTask.description || '',
      defaultProductivity: subTask.defaultProductivity,
      unit: subTask.unit,
      order: subTask.order,
      isActive: subTask.isActive
    });
    setIsEditing(true);
    onFormOpen();
  }, [onFormOpen]);

  const handleDeleteClick = useCallback((subTask) => {
    setSelectedSubTask(subTask);
    onDeleteOpen();
  }, [onDeleteOpen]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedSubTask) return;

    setSubmitting(true);
    try {
      const { data } = await api.delete(`/super-admin/master/subtasks/${selectedSubTask.id}`);

      if (data.success) {
        toast.success('Deleted');
        await fetchAllSubTasks();
        onDataChange?.();
        onDeleteClose();
      } else {
        toast.error(data.message || 'Failed');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete');
    } finally {
      setSubmitting(false);
    }
  }, [selectedSubTask, onDataChange, onDeleteClose, fetchAllSubTasks]);

  const handleSubmit = useCallback(async () => {
    if (!formData.name || !formData.masterActivityId) {
      toast.error('Name and activity required');
      return;
    }

    setSubmitting(true);
    try {
      const url = isEditing
        ? `/super-admin/master/subtasks/${selectedSubTask.id}`
        : `/super-admin/master/activities/${formData.masterActivityId}/subtasks`;

      const method = isEditing ? 'put' : 'post';
      const { data } = await api[method](url, formData);

      if (data.success) {
        toast.success(`${isEditing ? 'Updated' : 'Created'}`);
        await fetchAllSubTasks();
        onDataChange?.();
        onFormClose();
      } else {
        toast.error(data.message || 'Failed');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed');
    } finally {
      setSubmitting(false);
    }
  }, [formData, isEditing, selectedSubTask, onDataChange, onFormClose, fetchAllSubTasks]);

  const subTaskIds = useMemo(() => subTasks.map(s => s.id), [subTasks]);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="300px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Card bg="gray.800" borderColor="gray.700">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Heading size="md" color="white">Master Sub-Tasks</Heading>
              <Text color="gray.400" fontSize="sm">
                Drag rows or use arrows to reorder
              </Text>
            </VStack>
            <HStack spacing={3}>
              {hasOrderChanges && (
                <>
                  <Button leftIcon={<FiX />} size="sm" variant="outline" onClick={handleCancelOrder}>
                    Cancel
                  </Button>
                  <Button leftIcon={<FiSave />} size="sm" colorScheme="green" onClick={handleSaveOrder} isLoading={savingOrder}>
                    Save Order
                  </Button>
                </>
              )}
              <Button leftIcon={<FiPlus />} size="sm" colorScheme="blue" onClick={handleCreate} isDisabled={allActivities.length === 0}>
                Add
              </Button>
            </HStack>
          </Flex>
        </CardHeader>
      </Card>

      {hasOrderChanges && (
        <Alert status="warning" variant="left-accent">
          <AlertIcon />
          Unsaved changes
        </Alert>
      )}

      <Card bg="gray.800" borderColor="gray.700">
        <CardBody p={0}>
          {allActivities.length === 0 ? (
            <Box p={8}>
              <Alert status="info">
                <AlertIcon />
                Create categories and activities first
              </Alert>
            </Box>
          ) : subTasks.length === 0 ? (
            <Box p={8} textAlign="center">
              <Text color="gray.400" mb={4}>No sub-tasks</Text>
              <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={handleCreate}>
                Add Sub-Task
              </Button>
            </Box>
          ) : (
            <Box overflowX="auto" maxH="600px" overflowY="auto">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <Table variant="simple" size="sm">
                  <Thead position="sticky" top={0} bg="gray.800" zIndex={1}>
                    <Tr>
                      <Th color="gray.400" width="40px"></Th>
                      <Th color="gray.400" width="80px">Order</Th>
                      <Th color="gray.400">Category</Th>
                      <Th color="gray.400">Activity</Th>
                      <Th color="gray.400">Task Name</Th>
                      <Th color="gray.400">Productivity</Th>
                      <Th color="gray.400">Unit</Th>
                      <Th color="gray.400">Status</Th>
                      <Th color="gray.400">Actions</Th>
                    </Tr>
                  </Thead>
                  <SortableContext items={subTaskIds} strategy={verticalListSortingStrategy}>
                    <Tbody>
                      {subTasks.map((subTask, index) => (
                        <SortableRow
                          key={subTask.id}
                          subTask={subTask}
                          index={index}
                          totalCount={subTasks.length}
                          onEdit={handleEdit}
                          onDelete={handleDeleteClick}
                          onMoveUp={moveUp}
                          onMoveDown={moveDown}
                        />
                      ))}
                    </Tbody>
                  </SortableContext>
                </Table>
              </DndContext>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size="lg">
        <ModalOverlay />
        <ModalContent bg="gray.800" borderColor="gray.700">
          <ModalHeader color="white">{isEditing ? 'Edit' : 'Create'} Sub-Task</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel color="gray.300">Activity</FormLabel>
                <Select value={formData.masterActivityId} onChange={(e) => setFormData(prev => ({ ...prev, masterActivityId: e.target.value }))} bg="gray.700" color="white" borderColor="gray.600">
                  <option value="">Select</option>
                  {allActivities.map(activity => (
                    <option key={activity.id} value={activity.id}>
                      {activity.categoryCode} - {activity.code} - {activity.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel color="gray.300">Task Name</FormLabel>
                <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="Task name" bg="gray.700" color="white" borderColor="gray.600" />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300">Description</FormLabel>
                <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} bg="gray.700" color="white" borderColor="gray.600" rows={3} />
              </FormControl>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel color="gray.300">Productivity</FormLabel>
                  <Input 
                    type="number"
                    value={formData.defaultProductivity} 
                    onChange={(e) => {
                      const value = e.target.value;
                      // Store the raw value (including partial decimals like "3." or "0.5")
                      if (value === '') {
                        setFormData(prev => ({ ...prev, defaultProductivity: '' }));
                      } else {
                        // Allow any valid number input, including decimals
                        setFormData(prev => ({ ...prev, defaultProductivity: value }));
                      }
                    }}
                    onBlur={(e) => {
                      // Parse and validate only when user leaves the field
                      const value = e.target.value;
                      if (value === '' || value === '.') {
                        setFormData(prev => ({ ...prev, defaultProductivity: 0 }));
                      } else {
                        const parsed = parseFloat(value);
                        setFormData(prev => ({ 
                          ...prev, 
                          defaultProductivity: !isNaN(parsed) && parsed >= 0 ? parsed : 0 
                        }));
                      }
                    }}
                    min={0} 
                    step="0.01"
                    bg="gray.700" 
                    color="white" 
                    borderColor="gray.600"
                    placeholder="0.00"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color="gray.300">Unit</FormLabel>
                  <Select value={formData.unit} onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))} bg="gray.700" color="white" borderColor="gray.600">
                    <option value="No">No</option>
                    <option value="m">m</option>
                    <option value="Sq.m">Sq.m</option>
                    <option value="Item">Item</option>
                    <option value="Set">Set</option>
                  </Select>
                </FormControl>

                <FormControl width="120px">
                  <FormLabel color="gray.300">Order</FormLabel>
                  <NumberInput value={formData.order} onChange={(value) => setFormData(prev => ({ ...prev, order: parseInt(value) || 0 }))} min={0}>
                    <NumberInputField bg="gray.700" color="white" borderColor="gray.600" />
                  </NumberInput>
                </FormControl>
              </HStack>

              <FormControl display="flex" alignItems="center">
                <FormLabel color="gray.300" mb={0}>Active</FormLabel>
                <Switch isChecked={formData.isActive} onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))} colorScheme="green" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={submitting}>
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="gray.800" borderColor="gray.700">
          <ModalHeader color="white">Confirm Delete</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <Text color="gray.300">
              Delete <Text as="span" fontWeight="bold" color="white">{selectedSubTask?.name}</Text>?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={submitting}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default MasterSubTaskManager;
